import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import cx from 'classnames'
// utils
import { isEllipsisActive, restrictToRange } from 'utils'
import { PositionEnum } from 'types/cssTypes'

interface IProps {
  tooltipText: string | JSX.Element
  children: JSX.Element
  suppress?: boolean
  // this would make the tooltip to only be displayed when the text is cut off because of space limitation - default should be false
  showOnTextOverflow?: boolean
  tooltipStyles?: any
  childrenStyles?: any
  position?: PositionEnum
  positionContent?: number
  positionTooltipArrow?: number
  className?: string
}

interface IDimensions {
  tooltipWidth: number
  tooltipHeight: number
  childrenWidth: number
  childrenHeight: number
}

export const CustomTooltip = ({
  tooltipText,
  children,
  suppress,
  showOnTextOverflow = false,
  tooltipStyles,
  childrenStyles,
  position = PositionEnum.left,
  positionContent = 50,
  positionTooltipArrow = 50,
  className,
}: IProps) => {
  // local variables
  const initialDimensions = { tooltipWidth: 0, tooltipHeight: 0, childrenWidth: 0, childrenHeight: 0 }
  // local state
  const [show, setShow] = useState(false)
  const [dimenstions, setDimensions] = useState<IDimensions>(initialDimensions)
  const childrenContainer = useRef<HTMLDivElement>(null)
  const tooltipContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show && tooltipContainer.current && childrenContainer.current) {
      setDimensions((prev) => ({
        ...prev,
        tooltipWidth: tooltipContainer.current!.offsetWidth,
        tooltipHeight: tooltipContainer.current!.offsetHeight,
        childrenWidth: childrenContainer.current!.offsetWidth,
        childrenHeight: childrenContainer.current!.offsetHeight,
      }))
    }
  }, [show])

  if (suppress) {
    return <>{children}</>
  }

  return (
    <Div
      tooltipStyles={tooltipStyles}
      childrenStyles={childrenStyles}
      position={position}
      positionContent={positionContent}
      positionTooltipArrow={positionTooltipArrow}
      dimenstions={dimenstions}
      className={className}
      tooltipText={tooltipText}
    >
      <div
        ref={tooltipContainer}
        className={cx('tooltip-container', {
          visible: show,
        })}
      >
        <span>{tooltipText}</span>
        <span className="tooltip-arrow" />
      </div>
      <div
        ref={childrenContainer}
        className="tooltip-children-container"
        onMouseEnter={() => (showOnTextOverflow ? setShow(isEllipsisActive(childrenContainer.current)) : setShow(true))}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
    </Div>
  )
}

const Div = styled.div<{
  tooltipStyles: any
  childrenStyles: any
  position: PositionEnum
  positionContent: number
  positionTooltipArrow: number
  dimenstions: IDimensions
  tooltipText: string | JSX.Element
}>`
  position: relative;

  .tooltip-container {
    pointer-events: none;
    z-index: 1;
    display: none;
    position: absolute;
    max-width: 10rem;
    min-width: 5rem;
    border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    background: ${({ theme }) => theme.colors.neutral.lightest};
    color: ${({ theme }) => theme.colors.neutral.neutral};
    ${({ theme }) => theme.typography.xs.normal};
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    line-height: 0.85rem;
    padding: 0.75rem;
    border-radius: 0.3rem;
    ${({ theme, position, positionContent, dimenstions }) => {
      switch (position) {
        case PositionEnum.bottom:
          return css`
            bottom: ${() => `calc(-${theme.util.pixelToRem(dimenstions.childrenHeight)} - 1rem)`};
            left: ${() => theme.util.pixelToRem((dimenstions.childrenWidth * restrictToRange(positionContent)) / 100)};
            transform: translateX(-50%);
          `
        case PositionEnum.top:
          return css`
            bottom: ${() => `calc(${theme.util.pixelToRem(dimenstions.childrenHeight)} + 1rem)`};
            left: ${() => theme.util.pixelToRem((dimenstions.childrenWidth * restrictToRange(positionContent)) / 100)};
            transform: translateX(-50%);
          `
        case PositionEnum.left:
          return css`
            bottom: ${() =>
              `calc(-${theme.util.pixelToRem(dimenstions.tooltipHeight)} + ${theme.util.pixelToRem(
                (dimenstions.childrenHeight * restrictToRange(positionContent)) / 100,
              )})`};
            left: ${() => `calc(-${theme.util.pixelToRem(dimenstions.tooltipWidth)} - 1rem)`};
            transform: translateY(-50%);
          `
        case PositionEnum.right:
          css`
            bottom: ${() =>
              `calc(-${theme.util.pixelToRem(dimenstions.tooltipHeight)} + ${theme.util.pixelToRem(
                (dimenstions.childrenHeight * restrictToRange(positionContent)) / 100,
              )})`};
            left: ${() => `calc(${theme.util.pixelToRem(dimenstions.childrenWidth)} + 1rem)`};
            transform: translateY(-50%);
          `
      }
    }}
    ${({ tooltipStyles }) =>
      tooltipStyles &&
      css`
        ${tooltipStyles}
      `};
  }

  .tooltip-container.visible {
    display: ${({ tooltipText }) => (tooltipText ? 'inline-block' : 'none')};
  }

  .tooltip-arrow {
    position: absolute;
    border-width: 0.5rem;
    border-style: solid;
    ${({ theme, position, positionTooltipArrow, dimenstions }) => {
      switch (position) {
        case PositionEnum.bottom:
          return css`
            top: -1rem;
            left: ${() =>
              theme.util.pixelToRem((dimenstions.tooltipWidth * restrictToRange(positionTooltipArrow)) / 100)};
            border-color: transparent transparent ${({ theme }) => theme.colors.grey.lighter} transparent;
            transform: translateX(-50%);
            &:after {
              content: '';
              position: absolute;
              display: tooltipText ? 'block' : 'none';
              top: -0.45rem;
              border-width: 0.5rem;
              border-style: solid;
              border-color: transparent transparent ${({ theme }) => theme.colors.neutral.lightest} transparent;
              transform: translateX(-50%);
            }
          `
        case PositionEnum.top:
          return css`
            bottom: -1rem;
            left: ${() =>
              theme.util.pixelToRem((dimenstions.tooltipWidth * restrictToRange(positionTooltipArrow)) / 100)};
            border-color: ${({ theme }) => theme.colors.grey.lighter} transparent transparent transparent;
            transform: translateX(-50%);
            &:after {
              content: '';
              position: absolute;
              display: tooltipText ? 'block' : 'none';
              bottom: -0.45rem;
              border-width: 0.5rem;
              border-style: solid;
              border-color: ${({ theme }) => theme.colors.neutral.lightest} transparent transparent transparent;
              transform: translateX(-50%);
            }
          `
        case PositionEnum.left:
          return css`
            top: ${() =>
              theme.util.pixelToRem((dimenstions.tooltipHeight * restrictToRange(positionTooltipArrow)) / 100)};
            left: ${() => `calc(${theme.util.pixelToRem(dimenstions.tooltipWidth)} - 0.1rem)`};
            border-color: transparent transparent transparent ${({ theme }) => theme.colors.grey.lighter};
            transform: translateY(-50%);
            &:after {
              content: '';
              position: absolute;
              display: tooltipText ? 'block' : 'none';
              left: -0.575rem;
              border-width: 0.5rem;
              border-style: solid;
              border-color: transparent transparent transparent ${({ theme }) => theme.colors.neutral.lightest};
              transform: translateY(-50%);
            }
          `
        case PositionEnum.right:
          return css`
            top: ${() =>
              theme.util.pixelToRem((dimenstions.tooltipHeight * restrictToRange(positionTooltipArrow)) / 100)};
            left: -1rem;
            border-color: transparent ${({ theme }) => theme.colors.grey.lighter} transparent transparent;
            transform: translateY(-50%);
            &:after {
              content: '';
              position: absolute;
              display: tooltipText ? 'block' : 'none';
              left: -0.425rem;
              border-width: 0.5rem;
              border-style: solid;
              border-color: transparent ${({ theme }) => theme.colors.neutral.lightest} transparent transparent;
              transform: translateY(-50%);
            }
          `
      }
    }}
  }
  .tooltip-children-container {
    ${({ childrenStyles }) =>
      childrenStyles &&
      css`
        ${childrenStyles}
      `};
  }
`
